import {
  getBaseMerged,
  getLoadedBaseLanguages,
  i18n,
  loadLanguage,
  mergeLocaleFiles,
} from "./i18n";
import { Language } from "./i18n.types";

/**
 * Runtime terminology-override engine.
 *
 * Base locale files (`src/locales/<lang>/*.json`) carry the DEFAULT term wording
 * (tournament / athlete / venue). When a platform selects a non-default term, the
 * matching override bundle is deep-merged on top of the merged base bundle at
 * runtime via `i18n.addResourceBundle(..., deep, overwrite)`.
 *
 * Override files live at:
 *   `src/locales/_overrides/<option>/<lang>/<domain>.json`
 * and share the exact nested shape of the base domain files, containing only the
 * overridden leaves. They merge into a single bundle with the SAME logic as base
 * (`_misc.json` spread at top level, other domains under their filename key), so
 * we reuse `mergeLocaleFiles`.
 */

/** Override option keys (the non-default term selections that carry renamed wording). */
export type TerminologyOverrideOption = "race" | "event";

/**
 * Active, non-default term selections for a platform. A key is present only when
 * its term differs from the default; absent keys keep base wording.
 *
 * Only the tournament term is configurable for now; the engine itself is generic, so
 * athlete/venue can be re-added by extending this shape and `overrideOptionForTerm`.
 */
export type TerminologyOverrideOptions = {
  tournament?: "race" | "event";
};

type LocaleFileModule = { default: Record<string, unknown> };

// option/lang/domain — e.g. ../locales/_overrides/race/en-GB/tournament.json
const overrideFileLoaders = import.meta.glob<LocaleFileModule>("../locales/_overrides/*/*/*.json");

const overridePrefix = (option: TerminologyOverrideOption, lng: Language) =>
  `../locales/_overrides/${option}/${lng}/`;

const loadersForOverride = (option: TerminologyOverrideOption, lng: Language) => {
  const prefix = overridePrefix(option, lng);
  return Object.entries(overrideFileLoaders).filter(([p]) => p.startsWith(prefix));
};

// Memoized merged override bundles, keyed by `${option}:${lng}`.
const overrideBundleCache = new Map<string, Promise<Record<string, unknown>>>();

/**
 * Loads and merges the override domain files for a given option + language into a
 * single bundle (same merge logic as base). Memoized per (option, lng). Returns
 * `{}` when no override files are registered for that pair — e.g. an option that
 * isn't translated for a language yet. Never throws on a missing pair.
 */
export const loadOverrideBundle = (
  option: TerminologyOverrideOption,
  lng: Language
): Promise<Record<string, unknown>> => {
  const cacheKey = `${option}:${lng}`;
  const cached = overrideBundleCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async () => {
    const loaders = loadersForOverride(option, lng);
    if (!loaders.length) return {};
    const files = await Promise.all(
      loaders.map(async ([filePath, load]) => {
        const mod = await load();
        return [filePath, mod.default] as const;
      })
    );
    return mergeLocaleFiles(files);
  })();

  overrideBundleCache.set(cacheKey, promise);
  return promise;
};

/** The override option a term setting maps to, or `null` when it's the default. */
const overrideOptionForTerm = (
  options: TerminologyOverrideOptions
): TerminologyOverrideOption[] => {
  const active: TerminologyOverrideOption[] = [];
  if (options.tournament) active.push(options.tournament);
  return active;
};

const optionsKey = (lng: Language, options: TerminologyOverrideOptions) =>
  [lng, options.tournament ?? ""].join("|");

// Skip redundant re-application (same language + same active option set).
let lastAppliedKey: string | null = null;

/**
 * Applies the active terminology overrides for a language on top of the base
 * bundle, then forces react-i18next to re-render.
 *
 * 1. Ensure the base language is loaded.
 * 2. RESET: re-add the merged BASE bundle (deep + overwrite) so any previously
 *    applied override leaves are restored to default wording.
 * 3. For each active non-default option, load its bundle and deep-merge it on top.
 * 4. Emit `languageChanged` so react-i18next re-renders (addResourceBundle alone
 *    does NOT trigger a re-render).
 */
export const applyTerminologyOverrides = async (
  lng: Language,
  options: TerminologyOverrideOptions
): Promise<void> => {
  const key = optionsKey(lng, options);
  if (key === lastAppliedKey) return;

  await loadLanguage(lng);

  // RESET to base wording (undo any previously applied override).
  i18n.addResourceBundle(lng, "translation", getBaseMerged(lng), true, true);

  const activeOptions = overrideOptionForTerm(options);
  const bundles = await Promise.all(activeOptions.map((option) => loadOverrideBundle(option, lng)));
  for (const bundle of bundles) {
    if (Object.keys(bundle).length === 0) continue;
    i18n.addResourceBundle(lng, "translation", bundle, true, true);
  }

  lastAppliedKey = key;

  // react-i18next listens for this; addResourceBundle by itself won't re-render.
  i18n.emit("languageChanged", lng);
};

/**
 * Restores base (default) wording for every loaded language and clears the dedup
 * guard. Call on logout: `TerminologyOverridesGate` lives inside `AuthGuard`, so it
 * unmounts before its own reset effect can run — without this, the previous
 * platform's renamed terms stay merged into the global i18n bundle and leak into the
 * login screen and the next session.
 */
export const resetTerminologyOverrides = (): void => {
  for (const lng of getLoadedBaseLanguages()) {
    i18n.addResourceBundle(lng, "translation", getBaseMerged(lng), true, true);
  }
  lastAppliedKey = null;
  i18n.emit("languageChanged", i18n.language);
};
